import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { LLMProviderService } from '../utils/llmProvider';
import { TTSService, type utteranceData } from '../utils/ttsService';
import { WebLLMService } from '../utils/webllmService';
import { WhisperService } from '../utils/whisperService';
import type { Message } from '../types/message';
import { useAvatarAudio } from './useAvatarAudio';

// Function to play audio from base64 string or URL
/*const playAudioData = async (audioData: string): Promise<void> => {
  return new Promise((resolve, reject) => {
    try {
      let audioUrl: string;
      
      // Check if audioData is a full data URL (base64 with mime type prefix)
      if (audioData.startsWith('data:')) {
        audioUrl = audioData;
      }
      // Check if audioData is a plain base64 string (no prefix)
      else if (!audioData.startsWith('http') && !audioData.startsWith('blob:')) {
        // Assume it's base64, convert to data URL with common audio mime type
        audioUrl = `data:audio/wav;base64,${audioData}`;
      }
      // Otherwise assume it's a URL (http, https, or blob)
      else {
        audioUrl = audioData;
      }
      
      const audio = new Audio(audioUrl);
      
      audio.onended = () => {
        // Clean up blob URLs if we created one
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
        resolve();
      };
      
      audio.onerror = (e) => {
        if (audioUrl.startsWith('blob:')) {
          URL.revokeObjectURL(audioUrl);
        }
        console.log(e);
        reject(new Error('Audio playback failed'));
      };
      
      audio.play().catch(reject);
    } catch (error) {
      reject(error);
    }
  });
};*/

export function useChatIntegration(audioRef: React.RefObject<HTMLAudioElement | null>, sessionId?:string | null, avatarId?:string | null) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isTyping, setIsTyping] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [phonemes, setPhonemes] = useState<Array<{ phoneme: string; start: number; end: number }>>([]);
  //const [isOfflineMode, setIsOfflineMode] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [lastFailedMessage, setLastFailedMessage] = useState<string | null>(null);
  const ttsStartTimeRef = useRef<number>(0);
  //const audioRef = useRef<HTMLAudioElement | null>(null);

  const [speechSynthesisData,setSpeechSynthesisData]= useState<utteranceData>();
  const llmProvider = useMemo(() => new LLMProviderService(), []);
  const ttsService = useMemo(() => new TTSService(), []);
  const webllmService = useMemo(() => new WebLLMService(), []);
  const whisperService = useMemo(() => new WhisperService(), []);
  const {connectAudioElement, lipsync} = useAvatarAudio();

  // Initialize offline models if needed
  useEffect(() => {
    if (llmProvider.isOfflineCapable()) {
      webllmService.initialize().catch(console.error);
    }
  }, []);

  const addMessage = useCallback((content: string, type: 'user' | 'avatar' | 'system', isVoice = false) => {
    const newMessage: Message = {
      id: crypto.randomUUID(),
      type,
      content,
      timestamp: new Date(),
      isVoice,
    };
    setMessages(prev => [...prev, newMessage]);
  }, []);

  const generatePhonemes = useCallback(async (text: string): Promise<Array<{ phoneme: string; start: number; end: number }>> => {
    return await ttsService.generatePhonemes(text);
  }, [ttsService]);

  const base64ToBlobUrl = (base64:any) => {
  const binary = atob(base64);
  const array = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) array[i] = binary.charCodeAt(i);
  const blob = new Blob([array], { type: 'audio/wav' });
  return URL.createObjectURL(blob);
};



  const playAudio = useCallback(async (audioData?: string, text?: string): Promise<void> => {
    if (isMuted) return;
    console.log("plasy audio")
    setIsSpeaking(true);
    ttsStartTimeRef.current = Date.now();
    
    try {
      if (audioData) {
        // Play backend-generated audio (base64 or URL)
        if (!audioRef.current) return;

        const audio = audioRef.current;
        audio.crossOrigin = "anonymous";

        let audioUrl: string;
        // Check if audioData is a full data URL (base64 with mime type prefix)
        if (audioData.startsWith('data:')) {
          audioUrl = audioData;
        }
        // Check if audioData is a plain base64 string (no prefix)
        else if (!audioData.startsWith('http') && !audioData.startsWith('blob:')) {
          // Assume it's base64, convert to data URL with common audio mime type
          audioUrl = `data:audio/wav;base64,${audioData}`;
        }
        // Otherwise assume it's a URL (http, https, or blob)
        else {
          console.log("base 64 to blob url")
          audioUrl = base64ToBlobUrl(audioData);;
        }
        
        audioRef.current.src = audioUrl;
        audioRef.current.onloadedmetadata =() => {
          connectAudioElement(audioRef.current!);
        }
        audioRef.current.onplay =() => {
          console.log("playing audio")
          
        }
        // Clean up ref when audio ends
        audioRef.current.onended = () => {
          //audioRef.current = null;
          setIsSpeaking(false);
        };
        
        audioRef.current.onerror = () => {
          //audioRef = null;
          setIsSpeaking(false);
          throw new Error('Audio playback failed');
        };
        //
        //playFromBase64(audioData)
        
        //connectAudioElement(audioRef.current);
        
        
      
      }else if(audioRef.current){
        
        //connectAudioElement(audioRef.current);
        await audioRef.current.play();
      }
      else if (text) {
        // Fallback to TTS if no audio provided
        // For TTS, we don't have an audio element to sync with, so clear the ref
        //audioRef.current = null;
        await ttsService.speak(text,undefined,(data)=>{setSpeechSynthesisData(data)});
        setIsSpeaking(false);
      }
    } catch (error) {
      //audioRef.current = null;
      setIsSpeaking(false);
      throw error;
    }
  }, [ttsService, isMuted, connectAudioElement, audioRef]);

  /*const speakText = useCallback(async (text: string): Promise<void> => {
    if (isMuted) return;
    setIsSpeaking(true);
    
    ttsStartTimeRef.current = Date.now();
    try {
      await ttsService.speak(text, undefined, (data) => { setSpeechSynthesisData(data) });
    } finally {
      setIsSpeaking(false);
    }
  }, [ttsService, isMuted]);*/

const handleSendMessage = useCallback(async (text: string) => {
     if (!text.trim()) return;

     // Increment turn counter
     const turnCount = messages.length; // Previous messages count

     const userMessage: Message = {
       id: crypto.randomUUID(),
       type: 'user',
       content: text,
       timestamp: new Date(),
       isVoice: false,
     };

     setMessages(prev => [...prev, userMessage]);
     setIsTyping(true);
     setLastFailedMessage(null); // Clear any previous failure

     try {
       const chatMessages = [...messages, userMessage].map(msg => ({
         role: (msg.type === 'user' ? 'user' : msg.type === 'avatar' ? 'assistant' : 'system') as 'user' | 'assistant' | 'system',
         content: msg.content
       }));

       console.log('Sending to LLM:', chatMessages);
       const response = await llmProvider.chat(chatMessages,sessionId, avatarId, turnCount);
      

      if (!response.success) {
        throw new Error(response.error || 'Failed to get response');
      }

      // Use backend phonemes if available, otherwise generate locally
      const phonemesToUse = response.phonemes || await generatePhonemes(response.content);
      console.log(phonemesToUse)
      setPhonemes(phonemesToUse);

      // Add avatar message with audioUrl if available
      const avatarMessage: Message = {
        id: crypto.randomUUID(),
        type: 'avatar',
        content: response.content,
        timestamp: new Date(),
        isVoice: false,
        audioUrl: response.audio // Store audio for later replay
      };
      addMessage(avatarMessage.content, 'avatar', false);

      // Play audio using backend audio if available, otherwise fallback to TTS
      
      await playAudio(response.audio, response.content);
    } catch (error) {
      console.error('Error:', error);
      const fallbackMessage = import.meta.env.VITE_FALLBACK_MESSAGE ||
        'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta de nuevo.';
      addMessage(fallbackMessage, 'system');
      setLastFailedMessage(text); // Store the failed message for retry
    } finally {
      setIsTyping(false);
    }
  }, [addMessage, generatePhonemes, playAudio, isMuted, messages, llmProvider]);

  const replayLastAudio = useCallback(async () => {
    if (isMuted) return;
    const lastAvatarMessage = [...messages].reverse().find(m => m.type === 'avatar');
    if (lastAvatarMessage) {
      // Use stored audio if available, otherwise fallback to TTS
      await playAudio(lastAvatarMessage.audioUrl, lastAvatarMessage.content);
    }
  }, [messages, playAudio, isMuted]);
  
  // Expose audioRef for AvatarDisplay to use for lip-sync
  const getAudioRef = useCallback(() => audioRef, []);

  const getSpeechUtterance = useCallback(() => ttsService.getCurrentUtterance, []);
  

  const retryLastFailed = useCallback(async () => {
    if (lastFailedMessage) {
      setLastFailedMessage(null);
      await handleSendMessage(lastFailedMessage);
    }
  }, [lastFailedMessage, handleSendMessage]);

  const transcribeAudio = useCallback(async (audioBlob: Blob): Promise<string> => {
    try {
      if (llmProvider.isOfflineCapable()) {
        return await whisperService.transcribe(audioBlob);
      }
      
      if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
        const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
        const recognition = new SpeechRecognition();
        recognition.lang = 'es-CO';
        recognition.interimResults = false;

        return new Promise((resolve, reject) => {
          recognition.onresult = (event: any) => {
            resolve(event.results[0][0].transcript);
          };
          recognition.onerror = (event: any) => {
            reject(new Error(`Speech recognition error: ${event.error}`));
          };
          recognition.start();
        });
      }

      throw new Error('No speech recognition available');
    } catch (error) {
      console.error('Transcription error:', error);
      throw error;
    }
  }, [llmProvider, whisperService]);

  const stopAudio = useCallback(() => {
    // Stop backend audio if playing
    if (audioRef.current) {
      audioRef.current.pause();
    }
    // Stop TTS
    speechSynthesis.cancel();
  }, [audioRef]);

  return {
    messages,
    isTyping,
    isMuted,
    phonemes,
    //isOfflineMode,
    isSpeaking,
    lastFailedMessage,
    addMessage,
    handleSendMessage,
    transcribeAudio,
    stopAudio,
    setIsMuted,
    replayLastAudio,
    retryLastFailed,
    audioRef,
    lipsync,
    connectAudioElement,
    getSpeechUtterance,
    speechSynthesisData,
    getAudioRef,
  };
}
